// components/ScheduleEditor.jsx
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useHideHeader } from '../contexts/GlobalStateContext';
import './ScheduleEditor.css';

const ScheduleEditor = forwardRef(({
  originalHtml,
  editMode,
  onContentChange,
  fieldConflicts = [],
  typingUsers = {},
  editLocks = {},
  myLocks = {},
  onStartTyping,
  onStopTyping,
  onRequestLock,
  onReleaseLock,
  onBlockedToggle,
  onPlaceradChange, 
  station,
  currentDate,
  hideHeader: hideHeaderProp
}, ref) => {
  const { hideHeader: hideHeaderContext } = useHideHeader();
  const hideHeader = hideHeaderProp !== undefined ? hideHeaderProp : hideHeaderContext;
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [originalHtmlDocument, setOriginalHtmlDocument] = useState('');
  const [displayContent, setDisplayContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);

  const typingTimerRef = useRef(null);
  const MAX_CELL_LENGTH = 30;

  // entityId -> element, byggd en gång per innehålls-/lägesbyte (se effekten som sätter
  // innerHTML nedan) så lås-/typing-synken slipper querySelectorAll över hela dokumentet.
  const entityElementMapRef = useRef(new Map());
  // Senast applicerat lås-/typing-tillstånd per entityId, för diff mot nya SignalR-events.
  const prevLockTypingStateRef = useRef({});

  const normalizeTextValue = (value) => {
    if (!value) return '';
    value = value.replace(/<[^>]+>/g, '');
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    value = textarea.value;
    value = value.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
    value = value.replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g, '');
    value = value.replace(/[\u00A0\u2000-\u200A]/g, ' ');
    value = value.replace(/\u00AD/g, '');
    value = value.replace(/\s+/g, ' ').trim();
    return value;
  };

  const cleanReadonlyCells = () => {
    if (!contentRef.current) return;
    contentRef.current.querySelectorAll('*').forEach(element => {
      if (element.tagName === 'INPUT') return;
      const isEntityCell = element.hasAttribute('data-entity-id');
      const isPlaceradCell = element.hasAttribute('data-placerad-type');
      element.setAttribute('contenteditable',
        isEntityCell || (isPlaceradCell && editMode) ? 'true' : 'false'
      );
    });
  };


  const handleCopy = (e) => {
    const selection = window.getSelection();
    const text = normalizeTextValue(selection.toString());
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
  };

  const handleCut = (e) => {
    const selection = window.getSelection();
    const text = normalizeTextValue(selection.toString());
    e.clipboardData.setData('text/plain', text);
    if (selection.rangeCount > 0)
      selection.getRangeAt(0).deleteContents();
    if (onContentChange) {
      const element = selection.anchorNode?.parentElement?.closest('[data-entity-id]');
      if (element) onContentChange(element.dataset.entityId);
    }
    e.preventDefault();
  };

  const handlePaste = (e) => {
    if (!editMode) return;
    e.preventDefault();
    const text = normalizeTextValue(e.clipboardData.getData('text/plain'));
    document.execCommand('insertText', false, text);
  };

  useEffect(() => {
  }, [fieldConflicts]);

  // ===== SKALA =====
  useEffect(() => {
    const calculateScale = () => {
      if (!contentRef.current || !containerRef.current) return;
      if (!hideHeader) { setScale(1); return; }
      const contentHeight = contentRef.current.scrollHeight;
      const containerHeight = containerRef.current.clientHeight;
      setScale(Math.min((containerHeight * 0.95) / contentHeight, 1));
    };
    requestAnimationFrame(calculateScale);
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [displayContent, hideHeader]);

  // ===== LADDA HTML =====
  useEffect(() => {
    setLoading(true);
    ExtractBodyContent(originalHtml);
    setLoading(false);
  }, [originalHtml]);

  // ===== SÄTT innerHTML + koppla listeners =====
  useEffect(() => {
    if (contentRef.current && displayContent) {
      contentRef.current.innerHTML = displayContent;
      cleanReadonlyCells();
      if (editMode) {
        attachEventListeners();
      }

      // DOM:en är helt ny — bygg om entityId -> element-mappen och nollställ diff-state
      // som lås-/typing-effekten längre ner använder.
      const map = new Map();
      contentRef.current.querySelectorAll('[data-entity-id]').forEach(element => {
        map.set(element.dataset.entityId, element);
      });
      entityElementMapRef.current = map;
      prevLockTypingStateRef.current = {};
    }
  }, [displayContent, editMode]);

  const attachEventListeners = () => {
    if (!contentRef.current) return;

    const cells = contentRef.current.querySelectorAll('[data-entity-id]');
    cells.forEach(cell => {
      cell.removeEventListener('click', handleCellClick);
      cell.addEventListener('click', handleCellClick);
    });

    // ── Placerad-celler ──
    const placeradCells = contentRef.current.querySelectorAll('[data-placerad-type]');
    placeradCells.forEach(cell => {
      cell.removeEventListener('click', handlePlaceradClick);
      cell.removeEventListener('blur', handlePlaceradBlur);
      cell.addEventListener('click', handlePlaceradClick);
      cell.addEventListener('blur', handlePlaceradBlur);
    });
  };

  useEffect(() => {
    return () => {
      if (contentRef.current) {
        contentRef.current.querySelectorAll('[data-entity-id]')
          .forEach(cell => cell.removeEventListener('click', handleCellClick));
        contentRef.current.querySelectorAll('[data-placerad-type]')
          .forEach(cell => {
            cell.removeEventListener('click', handlePlaceradClick);
            cell.removeEventListener('blur', handlePlaceradBlur);
          });
      }
    };
  }, []);

  const handleCellClick = async (e) => {
    e.stopPropagation();

    const element = e.currentTarget;
    const entityId = element.dataset.entityId;
    if (!entityId) return;

    if (element.classList.contains('warning-triangel') ||
        element.classList.contains('red') ||
        element.hasAttribute('data-warning')) {
      element.classList.remove('warning-triangel', 'red');
      element.removeAttribute('data-warning');
    }

    if (myLocks[entityId]) {
      setSelectedEntityId(entityId);
      element.focus();
      return;
    }

    if (editLocks[entityId]) {
      alert(`Detta fält är låst av ${editLocks[entityId].lockedBy}`);
      return;
    }

    if (onRequestLock) {
      const result = await onRequestLock(station, currentDate, entityId);
      if (result.success) {
        setSelectedEntityId(entityId);
        element.classList.add('locked-by-me');
        element.focus();
      } else {
        alert(`Kunde inte låsa: ${result.message || 'Fältet är låst'}`);
      }
    }
  };

  const handlePlaceradClick = (e) => {
    e.stopPropagation(); // Hindrar blocked-toggle på raden
  };

const handlePlaceradBlur = (e) => {
  if (!editMode || !onPlaceradChange) return;
  const cell = e.currentTarget;
  const type = cell.dataset.placeradType;
  const namn = cell.dataset.placeradNamn;
  const placeradStation = cell.dataset.placeradStation;  // ← ny
  const placerad = cell.innerText.trim();
  onPlaceradChange(type, namn, placerad, placeradStation);  // ← skicka med
};

// ===== RAD-KLICK TOGGLE FÖR BLOCKED =====
const handleContentClick = async (e) => {
  const row = e.target.closest('tr[data-blocked-type]');
  if (row && editMode) {
    e.stopPropagation();
    const type = row.dataset.blockedType;
    const namn = row.dataset.blockedNamn;
    const blockedStation = row.dataset.blockedStation;
    const newBlocked = !row.classList.contains('blocked-row');
    row.classList.toggle('blocked-row', newBlocked);
    if (onBlockedToggle) {
      await onBlockedToggle(type, namn, newBlocked, blockedStation);
    }
    return;
  }

  await handleElementClick(e);
};

  const ExtractBodyContent = (html) => {
    setOriginalHtmlDocument(html);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    let bodyContent = doc.body.innerHTML;
    bodyContent = bodyContent.replace("<tbody><tr>", "<tr>");
    setDisplayContent(bodyContent);
  };

  // ===== IMPERATIVT API =====
  useImperativeHandle(ref, () => ({
    getEditedContent: () => {
      if (!contentRef.current) {
        console.error('❌ contentRef saknas');
        return { fullHtml: originalHtmlDocument, bodyContent: '' };
      }

      let editedBodyContent = contentRef.current.innerHTML;
      editedBodyContent = editedBodyContent.replace(/\u200B/g, '');

      if (!editedBodyContent || editedBodyContent.trim().startsWith('{')) {
        console.error('❌ getEditedContent: Ogiltigt innehåll');
        return { fullHtml: originalHtmlDocument, bodyContent: '' };
      }

      if (!originalHtmlDocument) {
        console.error('❌ originalHtmlDocument saknas');
        return { fullHtml: editedBodyContent, bodyContent: editedBodyContent };
      }

      const bodyTagMatch = originalHtmlDocument.match(/<body[^>]*>/i);
      if (!bodyTagMatch) {
        console.error('❌ Ingen <body>-tagg');
        return { fullHtml: editedBodyContent, bodyContent: editedBodyContent };
      }

      const bodyStartTag = bodyTagMatch[0];
      const bodyStartIndex = originalHtmlDocument.indexOf(bodyStartTag) + bodyStartTag.length;
      const bodyEndIndex = originalHtmlDocument.lastIndexOf('</body>');

      if (bodyEndIndex < 0) {
        console.error('❌ Ingen </body>-tagg');
        return { fullHtml: editedBodyContent, bodyContent: editedBodyContent };
      }

      let fullHtml =
        originalHtmlDocument.substring(0, bodyStartIndex) +
        editedBodyContent +
        originalHtmlDocument.substring(bodyEndIndex);

      fullHtml = fullHtml.replace('<tbody><tr>', '<tr>');
      return { fullHtml, bodyContent: editedBodyContent };
    },

    printReport: () => {
      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write(originalHtmlDocument);
      printWindow.document.close();
      printWindow.print();
    },

    contentRef: contentRef
  }));

  const handleElementClick = async (e) => {
    if (!editMode) return;

    const element = e.target.closest('[data-entity-id]');
    if (!element) return;

    const entityId = element.dataset.entityId;

    if (myLocks[entityId]) {
      setSelectedEntityId(entityId);
      return;
    }

    if (editLocks[entityId]) {
      alert(`Detta fält är låst av ${editLocks[entityId].lockedBy} till ${new Date(editLocks[entityId].expiresAt).toLocaleTimeString()}`);
      return;
    }

    if (onRequestLock) {
      const result = await onRequestLock(station, currentDate, entityId);
      if (result.success) {
        setSelectedEntityId(entityId);
      } else {
        alert(`Kunde inte låsa: ${result.message || 'Fältet är låst av annan användare'}`);
      }
    }
  };

  // ===== INPUT HANDLER =====
  const handleInput = (e) => {
    if (e.target?.tagName === 'INPUT') return;

    if (!editMode) return;

    const selection = window.getSelection();
    let element = null;

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      element = container.nodeType === Node.TEXT_NODE
        ? container.parentElement?.closest('[data-entity-id]')
        : container.closest('[data-entity-id]');
    }

    if (!element && document.activeElement)
      element = document.activeElement.closest('[data-entity-id]');

    if (!element) {
      if (onContentChange) onContentChange();
      return;
    }

    const entityId = element.dataset.entityId;
    const currentText = element.textContent || '';

    if (currentText.length === 0 || currentText.trim() === '') {
      if (currentText.length === 0) element.innerHTML = '\u200B';
      if (onContentChange) onContentChange(entityId);
    } else if (currentText.length > MAX_CELL_LENGTH) {
      console.log(`⚠️ Text too long (${currentText.length}), truncating`);
    } else {
      if (onContentChange) {
        console.log('📝 handleInput calling onContentChange with entityId:', entityId);
        onContentChange(entityId);
      }
    }

    const fieldId = `${station}-${currentDate}-${entityId}`;
    if (onStartTyping) onStartTyping(station, currentDate, fieldId);

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (onStopTyping) onStopTyping(station, currentDate, fieldId);
    }, 1000);
  };

  // ===== LÅS-SYNC =====
  useEffect(() => {
    if (!editMode && selectedEntityId && onReleaseLock) {
      onReleaseLock(station, currentDate, selectedEntityId);
      setSelectedEntityId(null);
    }
  }, [editMode, selectedEntityId, onReleaseLock, station, currentDate]);

  useEffect(() => {
    const elementById = entityElementMapRef.current;
    if (!elementById || elementById.size === 0) return;

    // typingUsers är nyckladt på "{station}-{currentDate}-{entityId}" — extrahera bara
    // entityId för det datum/station som just nu visas i denna editor-instans.
    const fieldPrefix = `${station}-${currentDate}-`;
    const typingEntityIds = new Set();
    for (const fieldId in typingUsers) {
      if (typingUsers[fieldId] && fieldId.startsWith(fieldPrefix)) {
        typingEntityIds.add(fieldId.slice(fieldPrefix.length));
      }
    }

    const prevState = prevLockTypingStateRef.current;
    const touchedEntityIds = new Set([
      ...Object.keys(prevState),
      ...Object.keys(myLocks),
      ...Object.keys(editLocks),
      ...typingEntityIds
    ]);

    const nextState = {};
    touchedEntityIds.forEach(entityId => {
      const lockClass = myLocks[entityId]
        ? 'locked-by-me'
        : editLocks[entityId]
          ? 'locked-by-other'
          : null;
      const isTyping = typingEntityIds.has(entityId);
      const prevEntry = prevState[entityId];

      // Inget ändrat för denna entitet sedan förra körningen — rör inte DOM:en.
      if (prevEntry && prevEntry.lockClass === lockClass && prevEntry.typing === isTyping) {
        nextState[entityId] = prevEntry;
        return;
      }

      const element = elementById.get(entityId);
      if (element) {
        element.classList.remove('locked-by-other', 'locked-by-me', 'typing-indicator');
        if (lockClass) {
          element.classList.add(lockClass);
        }
        if (lockClass === 'locked-by-other') {
          element.setAttribute('title', `Låst av ${editLocks[entityId].lockedBy}`);
        } else {
          element.removeAttribute('title');
        }
        if (isTyping) element.classList.add('typing-indicator');
      }

      nextState[entityId] = { lockClass, typing: isTyping };
    });

    prevLockTypingStateRef.current = nextState;
  }, [editLocks, myLocks, typingUsers, station, currentDate]);

  const handleKeyDown = (e) => {
    if (!editMode) return;

    const isInsideEditableCell = e.target.closest('[data-entity-id]');

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && ['x', 'c'].includes(e.key.toLowerCase())) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const startCell = range.startContainer.closest?.('[data-entity-id]');
        const endCell = range.endContainer.closest?.('[data-entity-id]');
        if (!startCell || !endCell || startCell !== endCell) {
          e.preventDefault();
          return;
        }
      }
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      const selection = window.getSelection();
      const cell = selection.anchorNode?.closest?.('[data-entity-id]');
      if (!cell) { e.preventDefault(); return; }
    }

    if (e.key === 'Enter' && !isInsideEditableCell) {
      e.preventDefault();
      handleElementClick(e);
    }
  };

  // ===== RENDER =====
  if (loading) {
    return <div className="editable-html-loading"><div>Laddar schema...</div></div>;
  }

  if (!displayContent) {
    return (
      <div className="editable-html-no-data">
        <p>Ingen data att visa för detta datum och område.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`editable-html-container${hideHeader ? ' hide-header' : ''}`}>
      <div
        className="editable-html-content-wrapper"
        style={hideHeader ? { transform: `scaleY(${scale})`, transformOrigin: 'top left' } : {}}
      >
        <div
          ref={contentRef}
          className={`editable-html-content ${editMode ? 'edit-mode' : 'view-mode'}`}
          contentEditable={editMode}
          onInput={handleInput}
          onClick={handleContentClick}
          onKeyDown={handleKeyDown}
          onCopy={handleCopy}
          onCut={handleCut}
          onPaste={handlePaste}
          role={editMode ? "textbox" : "article"}
          tabIndex={editMode ? 0 : -1}
          suppressContentEditableWarning={true}
        />
      </div>
    </div>
  );
});

ScheduleEditor.displayName = 'ScheduleEditor';
export default ScheduleEditor;
